// Frugal Navigator - Cart Packaging, Surcharge, and Upsell Logic
// Expanse theme - runs on /cart page

(function () {
  // ----- CONFIGURATION CONSTANTS -----
  const FREE_MAP_THRESHOLD = 60;          // Maps subtotal at or above this: no surcharge
  const FREE_SHIPPING_THRESHOLD = 60;     // Shopify's free standard ground threshold (cart subtotal)
  const UPSELL_START_SUBTOTAL = 41.07;    // Start upsell when cart subtotal >= this (maps + surcharge)
  const ROLLED_SURCHARGE_VARIANT_ID = 51047877443879; // Rolled variant ID
  const SMALL_SURCHARGE_VARIANT_ID = 51047877476647;  // Small Craft Fold variant ID

  const PACKAGE_TYPES = {
    NONE: 'none',
    COMPACT: 'compact',
    SMALL: 'small',
    ROLLED: 'rolled'
  };

  // ----- CORE CART ANALYSIS -----

  function analyzeCart(cart) {
    let mapSubtotalCents = 0;
    let hasRolledMaps = false;
    let hasSmallMaps = false;
    let hasCompactMaps = false;

    let surchargeRolledQty = 0;
    let surchargeSmallQty = 0;

    if (!cart || !Array.isArray(cart.items)) {
      return null;
    }

    cart.items.forEach(function (item) {
      // Handle surcharge items first
      if (item.variant_id === ROLLED_SURCHARGE_VARIANT_ID) {
        surchargeRolledQty = item.quantity || 0;
        return;
      }
      if (item.variant_id === SMALL_SURCHARGE_VARIANT_ID) {
        surchargeSmallQty = item.quantity || 0;
        return;
      }

      // Everything below here is for map items / classification by price ending

      // item.price is in cents; convert to dollars and look at last digit of cents
      const priceDollars = item.price / 100;
      const cents = priceDollars.toFixed(2).split('.')[1]; // e.g. "93", "95", "97"
      const lastDigit = parseInt(cents.slice(-1), 10);     // 3, 5, or 7

      if (lastDigit !== 3 && lastDigit !== 5 && lastDigit !== 7) {
        // Not one of our map price endings; ignore for packaging & maps subtotal
        return;
      }

      // Add to map subtotal
      mapSubtotalCents += item.line_price || 0;

      // Track packaging types present
      if (lastDigit === 7) {
        hasRolledMaps = true;
      } else if (lastDigit === 5) {
        hasSmallMaps = true;
      } else if (lastDigit === 3) {
        hasCompactMaps = true;
      }
    });

    const mapSubtotal = mapSubtotalCents / 100;
    const cartSubtotal = (cart.items_subtotal_price || 0) / 100;

    // Determine packaging for the whole cart, using "rolled wins" logic
    let packageType = PACKAGE_TYPES.NONE;
    if (hasRolledMaps) {
      packageType = PACKAGE_TYPES.ROLLED;
    } else if (hasSmallMaps) {
      packageType = PACKAGE_TYPES.SMALL;
    } else if (hasCompactMaps) {
      packageType = PACKAGE_TYPES.COMPACT;
    }

    // Decide which surcharge *should* apply, based on maps subtotal and packaging
    let needSurchargeType = null;
    if (mapSubtotal > 0 && mapSubtotal < FREE_MAP_THRESHOLD) {
      if (hasRolledMaps) {
        needSurchargeType = PACKAGE_TYPES.ROLLED; // rolled wins if present
      } else if (hasSmallMaps) {
        needSurchargeType = PACKAGE_TYPES.SMALL;
      }
      // Compact only or no maps -> no surcharge
    }

    // What surcharge is currently present?
    let currentSurchargeType = null;
    if (surchargeRolledQty > 0) {
      currentSurchargeType = PACKAGE_TYPES.ROLLED;
    } else if (surchargeSmallQty > 0) {
      currentSurchargeType = PACKAGE_TYPES.SMALL;
    }

    return {
      mapSubtotal: mapSubtotal,
      cartSubtotal: cartSubtotal,
      packageType: packageType,
      hasRolledMaps: hasRolledMaps,
      hasSmallMaps: hasSmallMaps,
      hasCompactMaps: hasCompactMaps,
      surchargeRolledQty: surchargeRolledQty,
      surchargeSmallQty: surchargeSmallQty,
      needSurchargeType: needSurchargeType,
      currentSurchargeType: currentSurchargeType
    };
  }

  // ----- CART UPDATE (SURCHARGES) -----

  function updateCartSurcharges(analysis, callback) {
    if (!analysis) {
      if (typeof callback === 'function') callback(null);
      return;
    }

    let desiredRolledQty = 0;
    let desiredSmallQty = 0;

    if (analysis.needSurchargeType === PACKAGE_TYPES.ROLLED) {
      desiredRolledQty = 1;
      desiredSmallQty = 0;
    } else if (analysis.needSurchargeType === PACKAGE_TYPES.SMALL) {
      desiredRolledQty = 0;
      desiredSmallQty = 1;
    } else {
      desiredRolledQty = 0;
      desiredSmallQty = 0;
    }

    const updates = {};
    let needsUpdate = false;

    if (analysis.surchargeRolledQty !== desiredRolledQty) {
      updates[ROLLED_SURCHARGE_VARIANT_ID] = desiredRolledQty;
      needsUpdate = true;
    }
    if (analysis.surchargeSmallQty !== desiredSmallQty) {
      updates[SMALL_SURCHARGE_VARIANT_ID] = desiredSmallQty;
      needsUpdate = true;
    }

    if (!needsUpdate) {
      if (typeof callback === 'function') callback(null);
      return;
    }

    const formData = new FormData();
    Object.keys(updates).forEach(function (variantId) {
      formData.append('updates[' + variantId + ']', updates[variantId]);
    });

    fetch('/cart/update.js', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (updatedCart) {
        // We just changed the surcharge line items. The theme's cart HTML
        // will not update itself, so force a one-time full reload so that:
        //  - the surcharge product row appears/disappears correctly, and
        //  - we can re-run our banner logic on the fresh cart.
        if (typeof window !== 'undefined' && window.location) {
          window.location.reload();
          return;
        }

        // Fallback: if for some reason we can't reload, at least call back.
        if (typeof callback === 'function') callback(updatedCart);
      })
      .catch(function () {
        if (typeof callback === 'function') callback(null);
      });
  }

  // ----- UI: SUMMARY + UPSELL BLOCK -----

  function renderMessages(analysis) {
    // Clean up old block
    const oldBlock = document.getElementById('frugal-shipping-messages');
    if (oldBlock && oldBlock.parentNode) {
      oldBlock.parentNode.removeChild(oldBlock);
    }

    if (!analysis) return;
    if (analysis.mapSubtotal <= 0) return; // No maps, nothing to explain or upsell

    const hasFoldedAlongside = analysis.hasSmallMaps || analysis.hasCompactMaps;

    // SKIP RULES:

    // 1) Compact-fold only (no rolled, no small-craft) -> no blue banner at all.
    if (analysis.packageType === PACKAGE_TYPES.COMPACT &&
        analysis.hasCompactMaps &&
        !analysis.hasRolledMaps &&
        !analysis.hasSmallMaps) {
      return;
    }

    // 2) Once map subtotal is at or above the free-map threshold:
    if (analysis.mapSubtotal >= FREE_MAP_THRESHOLD) {
      // a) Rolled-only larger orders: no banner (no tube surcharge, no mix to explain)
      if (analysis.packageType === PACKAGE_TYPES.ROLLED &&
          analysis.hasRolledMaps &&
          !hasFoldedAlongside) {
        return;
      }

      // b) Small-craft packaging with no current surcharge (small-craft only or small + compact):
      if (analysis.packageType === PACKAGE_TYPES.SMALL &&
          !analysis.currentSurchargeType) {
        return;
      }
      // NOTE: Mixed rolled + folded larger orders are intentionally NOT skipped,
      // so we can show the "we default to rolled; consider two orders" message.
    }

    const cartForm =
      document.querySelector('form[action="/cart"]') ||
      document.querySelector('.cart__items') ||
      document.querySelector('.cart');

    if (!cartForm || !cartForm.parentNode) {
      return;
    }

    const block = document.createElement('div');
    block.id = 'frugal-shipping-messages';
    block.style.background = '#f5f9ff';
    block.style.border = '1px solid #c8d8ff';
    block.style.padding = '14px 16px';
    block.style.margin = '16px 0';
    block.style.fontSize = '14px';
    block.style.lineHeight = '1.4';

    // 1) Packaging explanation
    const pExplain = document.createElement('p');
    pExplain.style.margin = '0 0 8px 0';

    if (analysis.packageType === PACKAGE_TYPES.ROLLED) {
      // ROLLED PACKAGING
      if (analysis.mapSubtotal >= FREE_MAP_THRESHOLD) {
        // Larger rolled orders – no tube surcharge.
        if (hasFoldedAlongside) {
          // Mixed rolled + folded, larger order: special "default to rolled" message.
          pExplain.textContent =
            'We noticed that you have both rolled and folded maps in your cart. To protect everything and keep the order together, we’ll ship all of these maps rolled in sturdy map tubes by default. If you would rather have some maps rolled and others folded, please consider placing those as two separate orders so we can package them differently.';
        } else {
          // Rolled-only larger order: this branch should normally be skipped by the rules above.
          pExplain.textContent =
            'Your cart includes rolled maps, which ship in sturdy, heavy-duty cardboard tubes to protect them in transit.';
        }
      } else {
        // Smaller rolled orders – WITH tube surcharge.
        if (hasFoldedAlongside) {
          // Mixed rolled + folded, smaller order, tube surcharge applied
          pExplain.textContent =
            'Your cart includes both rolled and folded maps. To protect everything and keep the order together, we ship all of these maps rolled in sturdy, heavy-duty tubes by default. Because this is a smaller map order, a one-time tube packaging charge has been added to help cover the cost of the tube; any folded maps simply ride along in the same tube. If you would prefer to receive some maps rolled and others folded, please consider placing those as two separate orders so we can package them differently.';
        } else {
          // Rolled only, smaller order, tube surcharge applied
          pExplain.textContent =
            'Your cart includes rolled maps, which ship in sturdy, heavy-duty cardboard tubes to protect them in transit. Because this is a smaller map order, a one-time tube packaging charge has been added to help cover the cost of the tube while keeping our map prices low. This tube charge disappears on larger map orders.';
        }
      }
    } else if (analysis.packageType === PACKAGE_TYPES.SMALL) {
      // SMALL-CRAFT PACKAGING (no rolled present)
      if (analysis.mapSubtotal >= FREE_MAP_THRESHOLD) {
        // Larger small-craft orders – no envelope surcharge (normally skipped above).
        pExplain.textContent =
          'Your cart includes small-craft fold maps that ship in a heavy-duty extra-large envelope so the charts can lie flat. Any compact-fold maps in the same order simply ride along in the same envelope.';
      } else {
        // Smaller small-craft orders – WITH envelope surcharge.
        pExplain.textContent =
          'Your cart includes at least one small-craft fold map, which ships in a heavy-duty extra-large envelope so the chart can lie flat. Because this is a smaller map order, a one-time envelope packaging charge has been added to help cover the cost of that special envelope while keeping our map prices low. Any compact-fold maps in the same order simply ride along in the same envelope. This envelope charge disappears on larger map orders.';
      }
    } else if (analysis.packageType === PACKAGE_TYPES.COMPACT) {
      // COMPACT-FOLD PACKAGING (rare cases, compact-only carts are skipped earlier)
      if (analysis.mapSubtotal >= FREE_MAP_THRESHOLD) {
        pExplain.textContent =
          'Your cart currently contains compact-fold maps that ship in a thin envelope at the carrier’s base rate. These items do not require any additional packaging charge.';
      } else {
        pExplain.textContent =
          'Your cart currently contains compact-fold maps that ship in a thin envelope at the carrier’s base rate, so there is no additional packaging charge for these items.';
      }
    } else {
      // Generic fallback
      pExplain.textContent =
        'Your cart currently includes map items. Packaging and related surcharges are calculated to keep our map prices as low as possible while matching the actual cost of the special tubes and envelopes we use.';
    }

    block.appendChild(pExplain);

    // 2) Upsell message (only when we're close to free shipping)
    const remaining = FREE_SHIPPING_THRESHOLD - analysis.cartSubtotal;
    const showUpsell =
      analysis.cartSubtotal >= UPSELL_START_SUBTOTAL &&
      analysis.cartSubtotal < FREE_SHIPPING_THRESHOLD &&
      remaining > 0.01;

    if (showUpsell) {
      const roundedRemaining = Math.max(0, Math.round(remaining * 100) / 100);
      const remainingText = roundedRemaining.toFixed(2);

      const pUpsell = document.createElement('p');
      pUpsell.style.margin = '4px 0 0 0';
      pUpsell.style.fontWeight = '500';

      if (analysis.currentSurchargeType === PACKAGE_TYPES.ROLLED ||
          analysis.currentSurchargeType === PACKAGE_TYPES.SMALL) {
        pUpsell.textContent =
          'You’re $' + remainingText + ' away from the point where we pick up standard ground shipping on map orders. ' +
          'Add another map and, in most cases, it will effectively ship for free: once you cross that line, the one-time packaging charge above disappears ' +
          'and standard ground shipping becomes free on qualifying orders.';
      } else {
        pUpsell.textContent =
          'You’re $' + remainingText + ' away from the point where we pick up standard ground shipping on map orders. ' +
          'Adding another map often means your extra map effectively ships for free, because you’ve crossed into our free-shipping range for map orders.';
      }

      block.appendChild(pUpsell);
    }

    // Insert block just after the cart form if possible, otherwise before
    if (cartForm.nextSibling) {
      cartForm.parentNode.insertBefore(block, cartForm.nextSibling);
    } else {
      cartForm.parentNode.appendChild(block);
    }
  }

  // ----- FETCH + PROCESS CART -----

  function fetchCartAndProcess() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        const analysis = analyzeCart(cart);
        if (!analysis) return;

        // First, correct surcharge line items if needed.
        // If a correction is needed, updateCartSurcharges will trigger a full reload.
        updateCartSurcharges(analysis, function (updatedCart) {
          // If no reload happened and no surcharge change was needed, just render messages.
          if (updatedCart && updatedCart.items) {
            const newAnalysis = analyzeCart(updatedCart);
            renderMessages(newAnalysis || analysis);
          } else {
            renderMessages(analysis);
          }
        });
      })
      .catch(function () {
        // Fail silently; don't block cart
      });
  }

  // ----- HELP: MAKE "CONTINUE SHOPPING" BUTTON RESPOND RELIABLY -----

  function fixContinueShoppingButtons() {
    // Expanse typically uses a link with class "cart__continue" for this
    var buttons = document.querySelectorAll('a.cart__continue');

    if (!buttons || !buttons.length) {
      return;
    }

    for (var i = 0; i < buttons.length; i++) {
      (function (btn) {
        // Avoid attaching multiple listeners to the same element
        if (btn.__frugalContinuePatched) {
          return;
        }
        btn.__frugalContinuePatched = true;

        var href = btn.getAttribute('href');
        if (!href || href === '#') {
          return;
        }

        btn.addEventListener('click', function () {
          // Force navigation immediately when clicked, even if some other
          // script interferes with the default link behavior.
          window.location.href = href;
        });
      })(buttons[i]);
    }
  }

  // ----- WATCH FOR QUANTITY CHANGES (PLUS / MINUS, MANUAL EDITS) -----

  let cartListenersAttachedTo = null;
  let quantityChangeTimeout = null;

  function scheduleCartRecheck() {
    if (quantityChangeTimeout) {
      clearTimeout(quantityChangeTimeout);
    }
    // Small delay so the theme can finish its own AJAX cart update first
    quantityChangeTimeout = setTimeout(function () {
      fetchCartAndProcess();
    }, 800);
  }

  function attachCartListeners() {
    const cartForm =
      document.querySelector('form[action="/cart"]') ||
      document.querySelector('.cart__items') ||
      document.querySelector('.cart');

    if (!cartForm) return;

    // If we're already attached to this specific form instance, skip
    if (cartListenersAttachedTo === cartForm) return;
    cartListenersAttachedTo = cartForm;

    // Listen for changes to quantity inputs (Shopify typically uses "updates[...]")
    cartForm.addEventListener('change', function (evt) {
      const target = evt.target;
      if (!target) return;
      if (target.name && target.name.indexOf('updates') === 0) {
        scheduleCartRecheck();
      }
    });

    // Listen for clicks on plus/minus quantity buttons/links inside the cart form
    cartForm.addEventListener('click', function (evt) {
      const btn = evt.target && evt.target.closest('button, a');
      if (!btn) return;

      const className = btn.className || '';
      // Heuristic: match common quantity-related classes
      if (/qty|quantity|cart__qty|cart-qty|qty__button/i.test(className)) {
        scheduleCartRecheck();
      }
    });
  }

  // ----- INITIALIZE ON CART PAGE -----

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    if (!window.location.pathname || window.location.pathname.indexOf('/cart') === -1) {
      return; // Only run this on the main cart page
    }

    attachCartListeners();
    fetchCartAndProcess();
    fixContinueShoppingButtons();

    // Re-run when Expanse (or other scripts) trigger an AJAX cart refresh
    document.addEventListener('cart:refresh', function () {
      if (!window.location.pathname || window.location.pathname.indexOf('/cart') === -1) {
        return;
      }
      attachCartListeners();
      fetchCartAndProcess();
      fixContinueShoppingButtons();
    });
  });
})();
